/** Content blocks (owner feedback F21): text, media and small displays that explain, decorate or identify. */
import { L, P, SIZE_LABELS, defineBlock } from './custom-blocks';
import { NOTIFY, PEOPLE, ex } from './custom-blocks-kit';

const SOURCES = ['records', 'static', 'insight'] as const;
const RICH_SAMPLE = L(
  '## Welcome\nA short guide to **how this works**.\n\n- Pick a week\n- Add your hours\n- Send them for approval\n\n> Tip: you can paste a table from a spreadsheet.',
  '## Willkommen\nEine kurze Anleitung, **wie das funktioniert**.\n\n- Woche wählen\n- Stunden eintragen\n- Zur Freigabe senden\n\n> Tipp: Sie können eine Tabelle aus einer Tabellenkalkulation einfügen.',
);

export const contentBlocks = {
  richtext: defineBlock({
    name: L('Rich text', 'Formatierter Text'),
    desc: L(
      'Text with headings, lists, bold, links and quotes, written in Markdown.',
      'Text mit Überschriften, Listen, Fettdruck, Links und Zitaten, in Markdown geschrieben.',
    ),
    family: 'content',
    icon: 'bold',
    keywords: ['markdown', 'paragraph', 'article', 'text block', 'formatting'],
    summary:
      'A block of Markdown: # headings, **bold**, *italic*, `code`, lists, > quotes, tables, [links](/o/people) inside the app. {{ expressions }} work inside it.',
    props: {
      text: P.tpl('Text', 'Text', { long: true, def: '' }),
      size: P.pick('Size', 'Größe', ['sm', 'md', 'lg'] as const, 'md', { labels: SIZE_LABELS }),
      tone: P.pick('Colour', 'Farbe', ['default', 'muted'] as const, 'default', {
        labels: { default: L('Normal', 'Normal'), muted: L('Muted', 'Gedämpft') },
      }),
    },
    example: { props: { text: RICH_SAMPLE } },
  }),

  image: defineBlock({
    name: L('Image', 'Bild'),
    desc: L(
      'A picture from a file field, an uploaded file or a data address, with a caption.',
      'Ein Bild aus einem Dateifeld, einer hochgeladenen Datei oder einer Daten-Adresse, mit Bildunterschrift.',
    ),
    family: 'content',
    icon: 'image',
    keywords: ['picture', 'photo', 'logo', 'banner', 'foto'],
    summary:
      'Shows an image. src is an expression that returns a file value (a record file field) or an in-app path or data:image address; nothing is loaded from outside.',
    props: {
      src: P.expr('Image', 'Bild', {
        expect: 'any',
        def: '',
        hint: L(
          'A file field of a record (row.photo), or a data:image address.',
          'Ein Dateifeld eines Datensatzes (row.foto) oder eine data:image-Adresse.',
        ),
      }),
      alt: P.tpl('Description for screen readers', 'Beschreibung für Screenreader', { opt: true }),
      caption: P.tpl('Caption', 'Bildunterschrift', { opt: true }),
      fit: P.pick('Fit', 'Einpassen', ['cover', 'contain'] as const, 'cover', {
        seg: true,
        labels: { cover: L('Fill', 'Füllen'), contain: L('Whole image', 'Ganzes Bild') },
      }),
      ratio: P.pick('Shape', 'Form', ['auto', '1/1', '4/3', '3/2', '16/9', '21/9'] as const, '16/9', {
        labels: { auto: L('Natural', 'Natürlich') },
      }),
      rounded: P.bool('Rounded corners', 'Runde Ecken', true),
    },
    example: {
      props: {
        src: '',
        alt: L('Sample picture', 'Beispielbild'),
        caption: L(
          'Pick a file field of a record, e.g. row.photo',
          'Wählen Sie ein Dateifeld eines Datensatzes, z. B. row.foto',
        ),
        ratio: '16/9',
      },
    },
  }),

  avatar: defineBlock({
    name: L('Avatar', 'Avatar'),
    desc: L(
      'A round picture with initials for a person, with name and a line of detail.',
      'Ein rundes Bild mit Initialen für eine Person, mit Name und einer Detailzeile.',
    ),
    family: 'content',
    icon: 'person',
    keywords: ['person', 'user', 'profile', 'member'],
    summary:
      'A person: initials in a coloured circle, optionally with the name and a second line. name is an expression (viewer.name, row.title).',
    props: {
      name: P.expr('Name', 'Name', { expect: 'text', def: 'viewer.name' }),
      subtitle: P.tpl('Second line', 'Zweite Zeile', { opt: true }),
      size: P.pick('Size', 'Größe', ['sm', 'md', 'lg', 'xl'] as const, 'lg', {
        labels: { ...SIZE_LABELS, xl: L('Extra large', 'Sehr groß') },
      }),
      showName: P.bool('Show the name', 'Namen zeigen', true),
    },
    example: {
      props: { name: '"Mara Klein"', subtitle: L('Head of Operations', 'Leiterin Betrieb'), size: 'lg' },
    },
  }),

  avatars: defineBlock({
    name: L('Avatar group', 'Avatar-Gruppe'),
    desc: L(
      'Overlapping avatars of several people, with +N for the rest.',
      'Überlappende Avatare mehrerer Personen, mit +N für den Rest.',
    ),
    family: 'content',
    icon: 'members',
    keywords: ['team', 'people', 'assignees', 'participants', 'stack'],
    summary:
      'A row of overlapping avatars, one per row of the source (name expression per row), "+N" past max.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', SOURCES),
      name: P.expr('Name of each', 'Name jeder Person', { expect: 'text', scope: 'row', def: 'row.title' }),
      max: P.num('Show up to', 'Höchstens zeigen', { def: 4, min: 2, max: 12, int: true }),
      size: P.pick('Size', 'Größe', ['sm', 'md', 'lg'] as const, 'md', { labels: SIZE_LABELS }),
    },
    example: { props: { source: 'sample', name: 'row.title', max: 4 }, data: [PEOPLE] },
  }),

  callout: defineBlock({
    name: L('Callout', 'Hinweisbox'),
    desc: L(
      'A highlighted box with an icon, a title, text and an optional button.',
      'Eine hervorgehobene Box mit Symbol, Titel, Text und optionaler Schaltfläche.',
    ),
    family: 'content',
    icon: 'alert',
    keywords: ['notice', 'banner', 'warning', 'tip', 'info', 'note'],
    summary:
      'An attention box (info, success, warning, danger, neutral) with title, text and an optional action button.',
    props: {
      tone: P.pick('Kind', 'Art', ['info', 'success', 'warning', 'danger', 'neutral'] as const, 'info', {
        labels: {
          info: L('Info', 'Info'),
          success: L('Success', 'Erfolg'),
          warning: L('Warning', 'Warnung'),
          danger: L('Problem', 'Problem'),
          neutral: L('Neutral', 'Neutral'),
        },
      }),
      title: P.tpl('Title', 'Titel', { opt: true }),
      text: P.tpl('Text', 'Text', { long: true, def: '' }),
      icon: P.icon(),
      actionLabel: P.tpl('Button label', 'Beschriftung der Schaltfläche', { opt: true }),
      action: P.call('Button action', 'Aktion der Schaltfläche', {
        when: { prop: 'actionLabel', in: ['__set'] },
      }),
    },
    example: {
      props: {
        tone: 'warning',
        title: L('Heads up', 'Zur Beachtung'),
        text: L('Requests for next week close on Friday.', 'Anträge für nächste Woche schließen am Freitag.'),
        actionLabel: L('Review', 'Prüfen'),
      },
    },
  }),

  emptystate: defineBlock({
    name: L('Empty state', 'Leerer Zustand'),
    desc: L(
      'A friendly "nothing here yet" with an icon, one sentence and the next step.',
      'Ein freundliches „Noch nichts da“ mit Symbol, einem Satz und dem nächsten Schritt.',
    ),
    family: 'content',
    icon: 'inbox',
    keywords: ['placeholder', 'no data', 'zero state', 'blank slate'],
    summary:
      'A centred empty state: icon, title, text and an optional primary button. Combine with visibleWhen to show it only when a source is empty.',
    props: {
      icon: P.icon({ en: 'Icon', de: 'Symbol' }),
      title: P.tpl('Title', 'Titel', { def: 'Nothing here yet' }),
      text: P.tpl('Text', 'Text', { long: true, opt: true }),
      size: P.pick('Size', 'Größe', ['sm', 'md', 'lg'] as const, 'md', { labels: SIZE_LABELS }),
      actionLabel: P.tpl('Button label', 'Beschriftung der Schaltfläche', { opt: true }),
      action: P.call('Button action', 'Aktion der Schaltfläche', {
        when: { prop: 'actionLabel', in: ['__set'] },
      }),
    },
    example: {
      props: {
        icon: 'inbox',
        title: L('No requests yet', 'Noch keine Anträge'),
        text: L(
          'When someone asks for time off it shows up here.',
          'Sobald jemand Urlaub beantragt, erscheint der Antrag hier.',
        ),
        actionLabel: L('New request', 'Neuer Antrag'),
      },
    },
  }),

  quote: defineBlock({
    name: L('Quote', 'Zitat'),
    desc: L(
      'A pull quote or testimonial with an author and a role.',
      'Ein hervorgehobenes Zitat oder eine Kundenstimme mit Autor und Rolle.',
    ),
    family: 'content',
    icon: 'quote',
    keywords: ['testimonial', 'blockquote', 'review', 'citation'],
    summary: 'A large quote with author, role and an avatar.',
    props: {
      text: P.tpl('Quote', 'Zitat', { long: true, def: '' }),
      author: P.tpl('Author', 'Autor', { opt: true }),
      role: P.tpl('Role or company', 'Rolle oder Firma', { opt: true }),
      avatar: P.bool('Show an avatar', 'Avatar zeigen', true),
      tone: P.pick('Look', 'Darstellung', ['plain', 'accent', 'card'] as const, 'accent', {
        labels: {
          plain: L('Plain', 'Schlicht'),
          accent: L('Accent line', 'Akzentlinie'),
          card: L('Card', 'Karte'),
        },
      }),
    },
    example: {
      props: {
        text: L(
          'We cut our weekly reporting from two hours to ten minutes.',
          'Wir haben unsere Wochenberichte von zwei Stunden auf zehn Minuten verkürzt.',
        ),
        author: L('Lena Fuchs', 'Lena Fuchs'),
        role: L('Operations, Example Co.', 'Betrieb, Example Co.'),
        tone: 'accent',
      },
    },
  }),

  codeblock: defineBlock({
    name: L('Code block', 'Codeblock'),
    desc: L(
      'Monospaced code or a snippet with line numbers and a copy button.',
      'Code oder Beispieltext in fester Breite mit Zeilennummern und Kopierknopf.',
    ),
    family: 'content',
    icon: 'code',
    keywords: ['snippet', 'monospace', 'terminal', 'json', 'sql', 'script'],
    summary: 'Preformatted monospaced text. It is shown, never run.',
    props: {
      code: P.str('Code', 'Code', { def: '', max: 8000, mono: true }),
      language: P.str('Language label', 'Sprachbezeichnung', {
        opt: true,
        max: 20,
        placeholder: L('e.g. json', 'z. B. json'),
      }),
      lineNumbers: P.bool('Line numbers', 'Zeilennummern', true),
      copy: P.bool('Copy button', 'Kopierknopf', true),
      wrap: P.bool('Wrap long lines', 'Lange Zeilen umbrechen', false),
    },
    example: {
      props: {
        code: '{\n  "week": "2026-W42",\n  "hours": 37.5,\n  "status": "submitted"\n}',
        language: 'json',
      },
    },
  }),

  qr: defineBlock({
    name: L('QR code', 'QR-Code'),
    desc: L(
      'A QR code for a link, a code or any text, drawn here: nothing leaves your system.',
      'Ein QR-Code für einen Link, eine Nummer oder einen Text, hier gezeichnet: nichts verlässt Ihr System.',
    ),
    family: 'content',
    icon: 'qr',
    keywords: ['qr', 'scan', 'code', 'link'],
    summary: 'A QR code of the text the value expression returns (up to 200 characters).',
    props: {
      value: P.expr('Content', 'Inhalt', {
        expect: 'text',
        def: '',
        hint: L(
          'A text or link, e.g. "https://…" or a record number.',
          'Ein Text oder Link, z. B. „https://…“ oder eine Nummer.',
        ),
      }),
      size: P.num('Size', 'Größe', { def: 160, min: 80, max: 480, int: true, unit: L('px', 'px') }),
      caption: P.tpl('Caption', 'Beschriftung', { opt: true }),
    },
    example: {
      props: {
        value: '"https://example.com/onboarding"',
        size: 140,
        caption: L('Scan to open the checklist', 'Scannen, um die Checkliste zu öffnen'),
      },
    },
  }),

  barcode: defineBlock({
    name: L('Barcode', 'Strichcode'),
    desc: L(
      'A Code 128 barcode for article numbers, passes and labels.',
      'Ein Code-128-Strichcode für Artikelnummern, Eintrittskarten und Etiketten.',
    ),
    family: 'content',
    icon: 'barcode',
    keywords: ['code128', 'ean', 'label', 'pass', 'scan'],
    summary: 'A Code 128 barcode of the text the value expression returns (letters, digits and punctuation).',
    props: {
      value: P.expr('Content', 'Inhalt', { expect: 'text', def: '' }),
      height: P.num('Height', 'Höhe', { def: 64, min: 32, max: 200, int: true, unit: L('px', 'px') }),
      showText: P.bool('Show the text below', 'Text darunter zeigen', true),
    },
    example: { props: { value: '"NL-2026-00417"', height: 60 } },
  }),

  embed: defineBlock({
    name: L('Embed', 'Einbettung'),
    desc: L(
      'A video, map or design file from an approved provider (YouTube, Vimeo, Loom, Figma, OpenStreetMap).',
      'Ein Video, eine Karte oder eine Designdatei eines freigegebenen Anbieters (YouTube, Vimeo, Loom, Figma, OpenStreetMap).',
    ),
    family: 'content',
    icon: 'embed',
    keywords: ['video', 'iframe', 'youtube', 'vimeo', 'map', 'figma', 'loom'],
    summary:
      'Embeds a page of an approved provider in a sandboxed frame. Only youtube-nocookie.com, player.vimeo.com, loom.com, figma.com and openstreetmap.org addresses are accepted.',
    props: {
      url: P.str('Address', 'Adresse', {
        def: '',
        max: 400,
        placeholder: L(
          'https://www.youtube-nocookie.com/embed/…',
          'https://www.youtube-nocookie.com/embed/…',
        ),
      }),
      title: P.tpl('Title (for screen readers)', 'Titel (für Screenreader)', { opt: true }),
      ratio: P.pick('Shape', 'Form', ['16/9', '4/3', '1/1', '21/9'] as const, '16/9', {}),
    },
    example: { props: { url: '', title: L('Embedded content', 'Eingebetteter Inhalt'), ratio: '16/9' } },
  }),

  icon: defineBlock({
    name: L('Icon', 'Symbol'),
    desc: L(
      'A single icon, optionally in a tinted tile.',
      'Ein einzelnes Symbol, wahlweise in einer getönten Kachel.',
    ),
    family: 'content',
    icon: 'sparkle',
    keywords: ['glyph', 'symbol', 'pictogram'],
    summary: 'One icon of the design system in a tone and size; tile puts it in a rounded tinted square.',
    props: {
      name: P.icon({ en: 'Icon', de: 'Symbol' }),
      size: P.pick('Size', 'Größe', ['sm', 'md', 'lg', 'xl'] as const, 'lg', {
        labels: { ...SIZE_LABELS, xl: L('Extra large', 'Sehr groß') },
      }),
      tone: P.tone('Colour', 'Farbe', 'accent'),
      tile: P.bool('In a tile', 'In einer Kachel', true),
      label: P.tpl('Name for screen readers', 'Name für Screenreader', { opt: true }),
    },
    example: { props: { name: 'calendar', tone: 'accent', tile: true, size: 'lg' } },
  }),

  link: defineBlock({
    name: L('Link', 'Link'),
    desc: L(
      'A text link that opens a record, a page of the app or runs an action.',
      'Ein Textlink, der einen Datensatz, eine Seite der App öffnet oder eine Aktion ausführt.',
    ),
    family: 'content',
    icon: 'link',
    keywords: ['anchor', 'navigate', 'open record', 'hyperlink'],
    summary:
      'A link. to is a click: open a record in the peek, run an action, or use path for a page of the app (/inbox, /o/people).',
    scopes: { row: 'source' },
    props: {
      label: P.tpl('Text', 'Text', { def: 'Open' }),
      path: P.str('Page of the app', 'Seite der App', {
        opt: true,
        max: 200,
        mono: true,
        placeholder: L('/inbox', '/inbox'),
        hint: L(
          'An in-app address starting with /. Leave empty to use the click below.',
          'Eine Adresse in der App, die mit / beginnt. Leer lassen, um den Klick unten zu nutzen.',
        ),
      }),
      to: P.click('Or when clicked', 'Oder beim Klick'),
      icon: P.icon(),
      tone: P.pick('Look', 'Darstellung', ['link', 'muted', 'button'] as const, 'link', {
        labels: {
          link: L('Accent link', 'Akzent-Link'),
          muted: L('Quiet link', 'Dezenter Link'),
          button: L('Like a button', 'Wie eine Schaltfläche'),
        },
      }),
      source: P.source('Rows for the address', 'Zeilen für die Adresse', ['records', 'static', 'insight'], {
        advanced: true,
        opt: true,
      }),
    },
    example: { props: { label: L('Go to the inbox', 'Zum Eingang'), path: '/inbox', icon: 'arrow-right' } },
  }),

  kbd: defineBlock({
    name: L('Keyboard keys', 'Tastenkürzel'),
    desc: L(
      'Keyboard shortcuts shown the way each platform writes them.',
      'Tastenkürzel, so geschrieben, wie es jede Plattform tut.',
    ),
    family: 'content',
    icon: 'keyboard',
    keywords: ['shortcut', 'hotkey', 'keys', 'keyboard'],
    summary: 'Shortcut keys such as "mod+k" (⌘K on a Mac, Strg+K elsewhere) with an optional explanation.',
    props: {
      keys: P.str('Keys', 'Tasten', {
        def: 'mod+k',
        max: 40,
        mono: true,
        hint: L('"mod+k" or a sequence "g h".', '„mod+k“ oder eine Folge „g h“.'),
      }),
      text: P.tpl('Explanation', 'Erklärung', { opt: true }),
    },
    example: { props: { keys: 'mod+k', text: L('Search everything', 'Alles durchsuchen') } },
  }),

  tags: defineBlock({
    name: L('Tag list', 'Tag-Liste'),
    desc: L(
      'A wrapped row of small labels from a list value or a data source.',
      'Eine umbrechende Reihe kleiner Marken aus einer Liste oder Datenquelle.',
    ),
    family: 'content',
    icon: 'tag',
    keywords: ['labels', 'chips', 'pills', 'keywords', 'categories'],
    summary:
      'Small labels. Either list = an expression returning a list of texts (state.tags), or source + text per row.',
    scopes: { row: 'source' },
    props: {
      list: P.expr('List value', 'Listenwert', {
        expect: 'list',
        opt: true,
        hint: L(
          'An expression that returns a list of texts, e.g. state.tags.',
          'Ein Ausdruck, der eine Liste von Texten liefert, z. B. state.tags.',
        ),
      }),
      source: P.source('Or data', 'Oder Daten', SOURCES, { advanced: true, opt: true }),
      text: P.expr('Text of each row', 'Text jeder Zeile', {
        expect: 'text',
        scope: 'row',
        def: 'row.title',
        advanced: true,
      }),
      tone: P.tone('Colour', 'Farbe', 'neutral'),
      max: P.num('Show up to', 'Höchstens zeigen', { def: 12, min: 1, max: 60, int: true }),
      empty: P.tpl('When empty', 'Wenn leer', { opt: true }),
    },
    example: { props: { list: '["Design","Research","Launch","Q4"]', tone: 'accent' } },
  }),

  keyvalue: defineBlock({
    name: L('Key-value list', 'Eigenschaftenliste'),
    desc: L(
      'Facts as label and value pairs, in one or two columns.',
      'Fakten als Paare aus Bezeichnung und Wert, in einer oder zwei Spalten.',
    ),
    family: 'content',
    icon: 'list',
    keywords: ['details', 'properties', 'facts', 'definition list', 'summary'],
    summary: 'A description list: each item has a label and a value text (both may use {{ expressions }}).',
    props: {
      items: P.items(
        'Rows',
        'Zeilen',
        {
          label: P.tpl('Label', 'Bezeichnung', { def: '' }),
          value: P.tpl('Value', 'Wert', { def: '' }),
          mono: P.bool('Monospaced', 'Festbreite', false),
        },
        {
          titleKey: 'label',
          max: 24,
          def: [{ label: 'Label', value: 'Value' }],
          addLabel: L('Add a row', 'Zeile hinzufügen'),
        },
      ),
      columns: P.pick('Columns', 'Spalten', ['1', '2'] as const, '1', { seg: true }),
      dense: P.bool('Compact', 'Kompakt', false),
    },
    example: {
      props: {
        columns: '2',
        items: [
          { label: L('Owner', 'Verantwortlich'), value: L('Mara Klein', 'Mara Klein') },
          { label: L('Status', 'Status'), value: L('In progress', 'In Arbeit') },
          { label: L('Start', 'Beginn'), value: L('12 Oct 2026', '12.10.2026'), mono: true },
          { label: L('Budget', 'Budget'), value: L('€ 48,000', '48.000 €'), mono: true },
        ],
      },
    },
  }),

  hero: defineBlock({
    name: L('Hero banner', 'Hero-Banner'),
    desc: L(
      'A large header with an eyebrow, a headline, a sentence and buttons.',
      'Ein großer Kopfbereich mit Vorzeile, Überschrift, Satz und Schaltflächen.',
    ),
    family: 'content',
    icon: 'hero',
    keywords: ['header', 'banner', 'welcome', 'landing', 'cover'],
    summary:
      'A prominent banner: eyebrow, title, subtitle on a tinted or dark surface. Its children (buttons) sit under the text.',
    children: 'many',
    props: {
      eyebrow: P.tpl('Eyebrow', 'Vorzeile', { opt: true }),
      title: P.tpl('Headline', 'Überschrift', { def: 'Welcome' }),
      subtitle: P.tpl('Sentence', 'Satz', { opt: true, long: true }),
      icon: P.icon(),
      look: P.pick('Look', 'Darstellung', ['accent', 'soft', 'dark', 'plain'] as const, 'accent', {
        labels: {
          accent: L('Accent', 'Akzent'),
          soft: L('Soft tint', 'Zarte Tönung'),
          dark: L('Dark', 'Dunkel'),
          plain: L('Plain', 'Schlicht'),
        },
      }),
      align: P.pick('Alignment', 'Ausrichtung', ['start', 'center'] as const, 'start', {
        seg: true,
        labels: { start: L('Left', 'Links'), center: L('Centre', 'Mitte') },
      }),
    },
    example: {
      actions: [NOTIFY],
      props: {
        eyebrow: L('Member portal', 'Mitgliederportal'),
        title: L('Welcome back, Lena', 'Willkommen zurück, Lena'),
        subtitle: L(
          'Your onboarding is 60 % done. Two steps left.',
          'Ihr Onboarding ist zu 60 % abgeschlossen. Noch zwei Schritte.',
        ),
        look: 'accent',
      },
      children: [ex.button('Continue', 'Weiter', 'primary')],
    },
  }),

  datetime: defineBlock({
    name: L('Date and time', 'Datum und Uhrzeit'),
    desc: L(
      'A date or time in the reader’s format, or "in 3 days".',
      'Ein Datum oder eine Uhrzeit im Format der Leserin oder des Lesers, oder „in 3 Tagen“.',
    ),
    family: 'content',
    icon: 'calendar',
    keywords: ['relative', 'timestamp', 'when', 'ago', 'countdown', 'due'],
    summary: 'Formats a date or date-time expression: date, time, date and time, or relative ("in 3 days").',
    props: {
      value: P.expr('Value', 'Wert', { expect: 'any', def: 'now' }),
      style: P.pick('Format', 'Format', ['date', 'time', 'datetime', 'relative'] as const, 'date', {
        labels: {
          date: L('Date', 'Datum'),
          time: L('Time', 'Uhrzeit'),
          datetime: L('Date and time', 'Datum und Uhrzeit'),
          relative: L('Relative', 'Relativ'),
        },
      }),
      prefix: P.tpl('Text before', 'Text davor', { opt: true }),
      size: P.pick('Size', 'Größe', ['sm', 'md', 'lg'] as const, 'md', { labels: SIZE_LABELS }),
    },
    example: { props: { value: 'now', style: 'datetime', prefix: L('Updated', 'Aktualisiert') } },
  }),

  trend: defineBlock({
    name: L('Trend', 'Trend'),
    desc: L(
      'A change versus a previous value with an arrow and a tone.',
      'Eine Veränderung gegenüber einem früheren Wert mit Pfeil und Farbton.',
    ),
    family: 'content',
    icon: 'pulse',
    keywords: ['delta', 'change', 'growth', 'up down', 'comparison'],
    summary:
      'Shows the change of value against previous (as a percentage or an absolute amount) with an up/down arrow; goodWhen says which direction is good.',
    props: {
      value: P.expr('Value', 'Wert', { expect: 'number', def: '0' }),
      previous: P.expr('Previous value', 'Früherer Wert', { expect: 'number', def: '0' }),
      mode: P.pick('Show', 'Anzeigen', ['percent', 'absolute'] as const, 'percent', {
        seg: true,
        labels: { percent: L('Percent', 'Prozent'), absolute: L('Difference', 'Differenz') },
      }),
      goodWhen: P.pick('Good when', 'Gut, wenn', ['up', 'down'] as const, 'up', {
        seg: true,
        labels: { up: L('It goes up', 'Es steigt'), down: L('It goes down', 'Es sinkt') },
      }),
      label: P.tpl('Text after', 'Text danach', { opt: true }),
    },
    example: { props: { value: '48', previous: '40', label: L('vs. last week', 'ggü. Vorwoche') } },
  }),
};
